import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtQty } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ChevronDown, ChevronRight, CalendarDays, TrendingUp, TrendingDown, StickyNote, Sparkles, Layers } from "lucide-react";
import { toast } from "sonner";

const ASSET_TYPES = ["all", "stock", "crypto", "option", "perp"];
const SIDES = ["all", "buy", "sell"];

const prettyDate = (d) => {
  const dt = new Date(`${d}T12:00:00Z`);
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const isToday = (d) => d === new Date().toISOString().slice(0, 10);

export default function TradeHistory() {
  const [days, setDays] = useState([]);
  const [totalPnl, setTotalPnl] = useState(0);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [digestOpen, setDigestOpen] = useState(true);
  const [openSpreads, setOpenSpreads] = useState(new Set());
  const [closingSpread, setClosingSpread] = useState(null);
  // Filters
  const [q, setQ] = useState("");
  const [assetType, setAssetType] = useState("all");
  const [side, setSide] = useState("all");

  useEffect(() => {
    let alive = true;
    api.get("/orders/history/by-day").then((r) => {
      if (!alive) return;
      setDays(r.data.days || []);
      setTotalPnl(r.data.total_realized_pnl || 0);
      // Auto-open first (most recent) day for quick glance
      if ((r.data.days || []).length) setOpen({ [r.data.days[0].date]: true });
    }).catch(() => {}).finally(() => setLoading(false));
    api.get("/journal/weekly").then((r) => setDigest(r.data)).catch(() => {});
    api.get("/options/spreads?status=open").then((r) => setOpenSpreads(new Set((r.data.spreads || []).map((s) => s.id)))).catch(() => {});
    return () => { alive = false; };
  }, []);

  const filteredDays = useMemo(() => {
    const term = q.trim().toLowerCase();
    return days
      .map((d) => {
        const trades = d.trades.filter((t) => {
          if (assetType !== "all" && t.asset_type !== assetType) return false;
          if (side !== "all" && t.side !== side) return false;
          if (term && !(t.ticker?.toLowerCase().includes(term) || t.name?.toLowerCase().includes(term) || t.symbol?.toLowerCase().includes(term))) return false;
          return true;
        });
        if (!trades.length) return null;
        // Recompute summary for the filtered slice so numbers match what's visible
        const buys = trades.filter((t) => t.side === "buy");
        const sells = trades.filter((t) => t.side === "sell");
        const volume = trades.reduce((s, t) => s + (t.total || 0), 0);
        const realized = sells.reduce((s, t) => s + (t.realized_pnl || 0), 0);
        const commission = trades.reduce((s, t) => s + (t.commission || 0), 0);
        const wins = sells.filter((t) => (t.realized_pnl || 0) > 0).length;
        const win_rate = sells.length ? (wins / sells.length) * 100 : null;
        return {
          ...d,
          trades,
          summary: {
            total_trades: trades.length,
            buys: buys.length,
            sells: sells.length,
            volume,
            commission,
            realized_pnl: realized,
            win_rate,
            symbols: [...new Set(trades.map((t) => t.ticker))].sort(),
          },
        };
      })
      .filter(Boolean);
  }, [days, q, assetType, side]);

  const filteredPnl = useMemo(() => filteredDays.reduce((s, d) => s + d.summary.realized_pnl, 0), [filteredDays]);

  const exportCsv = () => {
    const rows = [["date", "time", "ticker", "symbol", "asset_type", "side", "quantity", "price", "commission", "total", "realized_pnl", "route"]];
    filteredDays.forEach((d) => {
      d.trades.forEach((t) => {
        const dt = new Date(t.created_at);
        rows.push([
          dt.toISOString().slice(0, 10),
          dt.toISOString().slice(11, 19),
          t.ticker || "", t.symbol || "", t.asset_type || "",
          t.side || "", t.quantity, t.price, t.commission || 0, t.total || 0,
          t.realized_pnl || 0, t.route || "sim",
        ]);
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trade-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (date) => setOpen((cur) => ({ ...cur, [date]: !cur[date] }));

  return (
    <div className="space-y-6" data-testid="trade-history-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Journal</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-3">
            <CalendarDays className="w-7 h-7 md:w-8 md:h-8 opacity-70" />
            Trade History
          </h1>
          <div className="text-sm text-muted-foreground mt-1">Every fill grouped by day, with running P&amp;L so you can review what worked.</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Realized P&amp;L (filtered)</div>
            <div className={`font-mono-tab text-2xl font-medium ${filteredPnl >= 0 ? "gain" : "loss"}`} data-testid="total-realized-pnl">
              {filteredPnl >= 0 ? "+" : ""}{fmtMoney(filteredPnl)}
            </div>
          </div>
          <Button onClick={exportCsv} variant="outline" size="sm" className="rounded-full" data-testid="export-csv-btn"
                  disabled={!filteredDays.length}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Weekly digest — AI grade + coaching for the last 7 days */}
      {digest && digest.summary?.total_trades > 0 && (
        <Card className="rounded-xl border-[hsl(var(--ai))]/25 bg-gradient-to-br from-[hsl(var(--ai))]/10 to-transparent overflow-hidden"
              data-testid="weekly-digest">
          <button onClick={() => setDigestOpen((v) => !v)}
                  data-testid="digest-toggle"
                  className="w-full p-4 md:p-5 text-left flex items-center gap-4 hover:bg-[hsl(var(--ai))]/5">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center bg-[hsl(var(--ai))] text-black font-display font-bold text-xl md:text-2xl shrink-0" data-testid="digest-grade">
              {digest.ai?.grade || "—"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--ai))] flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Weekly recap · last 7 days
              </div>
              <div className="text-sm md:text-base font-medium mt-0.5 truncate">
                {digest.ai?.headline || `${digest.summary.closes} closes · ${digest.summary.win_rate ?? 0}% win rate`}
              </div>
              <div className={`text-sm font-mono-tab mt-0.5 ${digest.summary.realized_pnl >= 0 ? "gain" : "loss"}`}>
                {digest.summary.realized_pnl >= 0 ? "+" : ""}{fmtMoney(digest.summary.realized_pnl)} realized
                <span className="text-muted-foreground ml-2 not-italic">
                  · {digest.summary.total_trades} trades · vol {fmtMoney(digest.summary.volume, 0)}
                </span>
              </div>
            </div>
            {digestOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </button>
          {digestOpen && (
            <div className="border-t border-border/50 p-4 md:p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="digest-details">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--gain))] mb-1">What worked</div>
                <div className="text-sm">{digest.ai?.wins || "Not enough closes to grade — add a note or two on your trades for sharper coaching."}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--loss))] mb-1">What to fix</div>
                <div className="text-sm">{digest.ai?.improvements || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Next-week focus</div>
                <div className="text-sm italic">{digest.ai?.next_focus || "—"}</div>
              </div>
              {(digest.best || digest.worst) && (
                <div className="md:col-span-3 grid grid-cols-2 gap-3 pt-3 border-t border-border/50 text-xs">
                  {digest.best && (
                    <div data-testid="digest-best">
                      <span className="text-muted-foreground uppercase text-[10px] tracking-wider">Best trade </span>
                      <span className="font-mono-tab">{digest.best.ticker} </span>
                      <span className="gain font-mono-tab">+{fmtMoney(digest.best.realized_pnl)}</span>
                    </div>
                  )}
                  {digest.worst && (
                    <div data-testid="digest-worst" className="text-right">
                      <span className="text-muted-foreground uppercase text-[10px] tracking-wider">Worst </span>
                      <span className="font-mono-tab">{digest.worst.ticker} </span>
                      <span className="loss font-mono-tab">{fmtMoney(digest.worst.realized_pnl)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 rounded-xl border-border" data-testid="filters-card">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">
          <div className="sm:col-span-2">
            <Input placeholder="Search ticker or name..." value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="filter-search" className="rounded-full" />
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="filter-asset">
            {ASSET_TYPES.map((a) => (
              <button key={a} onClick={() => setAssetType(a)} data-testid={`filter-asset-${a}`}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors capitalize ${assetType === a ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {a}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="filter-side">
            {SIDES.map((s) => (
              <button key={s} onClick={() => setSide(s)} data-testid={`filter-side-${s}`}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors capitalize ${side === s ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="p-16 text-center border-border rounded-xl text-sm text-muted-foreground">Loading history…</Card>
      ) : !filteredDays.length ? (
        <Card className="p-16 text-center border-border rounded-xl" data-testid="empty-history">
          <div className="text-sm text-muted-foreground">No trades match your filters yet.</div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredDays.map((d) => {
            const s = d.summary;
            const pnlPositive = s.realized_pnl >= 0;
            const isOpen = !!open[d.date];
            return (
              <Card key={d.date} className="rounded-xl border-border overflow-hidden" data-testid={`day-card-${d.date}`}>
                <button onClick={() => toggle(d.date)}
                        data-testid={`day-toggle-${d.date}`}
                        className="w-full flex flex-wrap items-center gap-3 md:gap-6 px-4 md:px-5 py-4 text-left hover:bg-secondary/40 transition-colors">
                  <div className="flex items-center gap-2 min-w-[10rem]">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <div>
                      <div className="font-medium text-sm md:text-base">
                        {prettyDate(d.date)}
                        {isToday(d.date) && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-1.5 py-0.5">Today</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[16rem]">{s.symbols.slice(0, 4).join(" · ")}{s.symbols.length > 4 ? ` +${s.symbols.length - 4}` : ""}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 ml-auto text-right w-full md:w-auto">
                    <Stat label="Trades" value={s.total_trades} sub={`${s.buys}B · ${s.sells}S`} />
                    <Stat label="Volume" value={fmtMoney(s.volume, 0)} sub={s.commission ? `fees ${fmtMoney(s.commission)}` : "—"} />
                    <Stat label="Win rate" value={s.win_rate === null ? "—" : `${s.win_rate.toFixed(0)}%`}
                          sub={s.sells ? `${s.sells} closes` : "no sells"} />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Realized</div>
                      <div className={`font-mono-tab text-base md:text-lg font-medium flex items-center justify-end gap-1 ${pnlPositive ? "gain" : "loss"}`}
                           data-testid={`day-pnl-${d.date}`}>
                        {pnlPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        {pnlPositive ? "+" : ""}{fmtMoney(s.realized_pnl)}
                      </div>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-border" data-testid={`day-trades-${d.date}`}>
                    <div className="hidden sm:grid grid-cols-12 text-[10px] uppercase tracking-wider text-muted-foreground px-4 md:px-5 py-2 bg-secondary/30">
                      <div className="col-span-2">Time</div>
                      <div className="col-span-3">Asset</div>
                      <div className="col-span-1">Side</div>
                      <div className="col-span-2 text-right">Qty · Price</div>
                      <div className="col-span-2 text-right">Total</div>
                      <div className="col-span-2 text-right">Realized</div>
                    </div>
                    <div className="divide-y divide-border">
                      {d.trades.map((t) => {
                        const dt = new Date(t.created_at);
                        const pnl = t.realized_pnl || 0;
                        return (
                          <div key={t.id} className="grid grid-cols-2 sm:grid-cols-12 gap-y-1 items-center px-4 md:px-5 py-3 hover:bg-secondary/40" data-testid={`trade-row-${t.id}`}>
                            <div className="hidden sm:block sm:col-span-2 text-xs font-mono-tab text-muted-foreground">{dt.toLocaleTimeString()}</div>
                            <div className="sm:col-span-3">
                              <div className="font-medium text-sm">{t.ticker}
                                <span className="ml-2 text-[10px] uppercase text-muted-foreground border border-border rounded px-1 py-0.5">{t.asset_type}</span>
                                {t.spread_label && (
                                  <span data-testid={`spread-label-${t.id}`}
                                        className="ml-2 text-[10px] uppercase text-[hsl(var(--ai))] border border-[hsl(var(--ai))]/40 rounded px-1 py-0.5 bg-[hsl(var(--ai))]/10">
                                    {t.spread_label} · leg {(t.spread_leg_index ?? 0) + 1}/{t.spread_leg_total ?? 1}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{t.name}</div>
                            </div>
                            <div className="col-start-2 row-start-1 sm:col-start-auto sm:row-start-auto sm:col-span-1 text-right sm:text-left">
                              <span className={`text-xs uppercase font-medium px-2 py-0.5 rounded-full ${t.side === "buy" ? "bg-[hsl(var(--gain))]/15 gain" : "bg-[hsl(var(--loss))]/15 loss"}`}>
                                {t.side}
                              </span>
                            </div>
                            <div className="sm:col-span-2 sm:text-right text-xs font-mono-tab">
                              <span className="sm:hidden text-muted-foreground">Qty </span>{fmtQty(t.quantity)}
                              <span className="hidden sm:inline text-muted-foreground"> @ </span>
                              <div className="sm:inline text-muted-foreground sm:text-foreground">{fmtMoney(t.price)}</div>
                            </div>
                            <div className="sm:col-span-2 text-right font-mono-tab text-sm">
                              {fmtMoney(t.total)}
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t.route === "ibkr_paper" ? "IBKR paper" : t.route === "ibkr_live" ? "IBKR live" : "Sim"}
                              </div>
                            </div>
                            <div className="col-span-2 sm:col-span-2 text-right font-mono-tab text-sm">
                              {t.side === "sell" ? (
                                <span className={pnl >= 0 ? "gain" : "loss"}>{pnl >= 0 ? "+" : ""}{fmtMoney(pnl)}</span>
                              ) : (
                                <span className="text-muted-foreground text-xs">open</span>
                              )}
                              {t.side === "sell" && (
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                  {pnl >= 0 ? "win" : "loss"}
                                </div>
                              )}
                            </div>
                            <NoteRow
                              trade={t}
                              onSaved={(id, notes) => {
                                setDays((prev) => prev.map((day) => ({
                                  ...day,
                                  trades: day.trades.map((tr) => tr.id === id ? { ...tr, notes } : tr),
                                })));
                              }}
                            />
                            {t.spread_id && t.spread_leg_index === 0 && !t.is_close && openSpreads.has(t.spread_id) && (
                              <div className="col-span-2 sm:col-span-12 mt-1 flex items-center gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`close-spread-${t.spread_id}`}
                                  disabled={closingSpread === t.spread_id}
                                  onClick={async () => {
                                    setClosingSpread(t.spread_id);
                                    try {
                                      const { data: res } = await api.post(`/options/spread/${t.spread_id}/close`);
                                      toast.success(
                                        `Spread closed · realized ${res.realized_pnl >= 0 ? "+" : ""}$${res.realized_pnl.toFixed(2)}`
                                      );
                                      // Refresh
                                      const [hist, spreads] = await Promise.all([
                                        api.get("/orders/history/by-day"),
                                        api.get("/options/spreads?status=open"),
                                      ]);
                                      setDays(hist.data.days || []);
                                      setOpenSpreads(new Set((spreads.data.spreads || []).map((s) => s.id)));
                                    } catch (e) {
                                      toast.error(e?.response?.data?.detail || "Close failed");
                                    } finally {
                                      setClosingSpread(null);
                                    }
                                  }}
                                  className="rounded-full text-[11px] px-3 flex items-center gap-1 border-[hsl(var(--ai))]/40 text-[hsl(var(--ai))] hover:bg-[hsl(var(--ai))]/10">
                                  <Layers className="w-3 h-3" />
                                  {closingSpread === t.spread_id ? "Closing…" : "Close spread at mid"}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground pt-2">
        Realized P&amp;L uses a running weighted-average cost per symbol across your full history.
        Total across all days on file: <span className={totalPnl >= 0 ? "gain" : "loss"}>{totalPnl >= 0 ? "+" : ""}{fmtMoney(totalPnl)}</span>.
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-mono-tab text-sm md:text-base">{value}</div>
    <div className="text-[10px] text-muted-foreground">{sub}</div>
  </div>
);

const NoteRow = ({ trade, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(trade.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(trade.notes || ""), [trade.notes]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/orders/${trade.id}/notes`, { notes: draft });
      toast.success("Note saved");
      onSaved?.(trade.id, draft);
      setEditing(false);
    } catch (e) {
      toast.error("Failed to save note");
    } finally { setSaving(false); }
  };

  if (!editing && !trade.notes) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        data-testid={`add-note-${trade.id}`}
        className="col-span-2 sm:col-span-12 mt-1 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground w-fit">
        <StickyNote className="w-3 h-3" /> Add a note
      </button>
    );
  }
  if (!editing) {
    return (
      <div className="col-span-2 sm:col-span-12 mt-1 flex items-start gap-2 text-[11px] text-muted-foreground italic"
           data-testid={`note-view-${trade.id}`}>
        <StickyNote className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span className="flex-1 whitespace-pre-wrap">"{trade.notes}"</span>
        <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                data-testid={`edit-note-${trade.id}`}
                className="text-muted-foreground hover:text-foreground not-italic underline">edit</button>
      </div>
    );
  }
  return (
    <div className="col-span-2 sm:col-span-12 mt-1 flex flex-col sm:flex-row gap-2" onClick={(e) => e.stopPropagation()}
         data-testid={`note-editor-${trade.id}`}>
      <Input
        value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        placeholder="Why did you take this trade? (thesis, setup, catalyst)"
        maxLength={1000} autoFocus
        className="text-xs rounded-md" data-testid={`note-input-${trade.id}`} />
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(trade.notes || ""); }}
                className="rounded-full text-[11px] px-3">Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid={`save-note-${trade.id}`}
                className="rounded-full text-[11px] px-3 bg-foreground text-background hover:bg-foreground/90">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
};
